import { useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import {
  CollapsibleBlock,
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import {
  LineGrid,
  LineGridBody,
  LineGridHead,
  LineGridRow,
  LineGridTd,
  LineGridTh,
  blockNumberWheel,
  cellControlClass,
} from "@/components/shared/line-grid";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useInventoryBatches } from "@/features/inventory/api";
import { useMachines, useProcessTypes } from "@/features/machines/api";
import { useMaterialGrades, useMaterials, useShapeSizes } from "@/features/materials/api";
import {
  planToForm,
  useCreateProcessPlan,
  useProcessPlan,
  useUpdateProcessPlan,
} from "@/features/planning/api";
import { canEditPlan, INPUT_SOURCES } from "@/features/planning/constants";
import { MillStageAssist } from "@/features/planning/mill-stage-assist";
import { StageInventoryPicker } from "@/features/planning/stage-inventory-picker";
import {
  emptyPlanInput,
  emptyPlanOutput,
  emptyPlanStage,
  emptyProcessPlanForm,
  processPlanFormSchema,
  type ProcessPlanFormValues,
} from "@/features/planning/schema";
import { SuggestPlanButton } from "@/features/planning/suggest-plan";
import { useSalesOrder, useSalesOrders } from "@/features/sales-orders/api";
import { filterCompatibleInventory, PLANNING_SELECTABLE_STATUSES, sortFifo, type PlanningStockBatch } from "@/lib/mill";
import { cn } from "@/lib/utils";

export function PlanningNewPage() {
  return (
    <ErrorBoundary title="Plan form crashed">
      <PlanningFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function PlanningEditPage({ planId }: { planId: string }) {
  return (
    <ErrorBoundary title="Plan form crashed">
      <PlanningFormPage mode="edit" planId={planId} />
    </ErrorBoundary>
  );
}

function PlanningFormPage({ mode, planId }: { mode: "create" | "edit"; planId?: string }) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const { data: plan, isLoading } = useProcessPlan(isEdit ? planId : undefined);
  const { data: processTypes = [] } = useProcessTypes();
  const { data: machines = [] } = useMachines();
  const { data: materials = [] } = useMaterials();
  const { data: grades = [] } = useMaterialGrades();
  const { data: salesOrders = [] } = useSalesOrders();
  const { data: stockBatchesRaw = [] } = useInventoryBatches({
    batchStatuses: [...PLANNING_SELECTABLE_STATUSES],
  });
  const stockBatches = useMemo(() => sortFifo(stockBatchesRaw), [stockBatchesRaw]);
  const { data: allShapes = [] } = useShapeSizes();
  const createPlan = useCreateProcessPlan();
  const updatePlan = useUpdateProcessPlan();
  const hydratedRef = useRef(false);
  const [activeStage, setActiveStage] = useState(0);

  const form = useForm<ProcessPlanFormValues>({
    resolver: zodResolver(processPlanFormSchema),
    defaultValues: emptyProcessPlanForm,
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: "stages" });
  const values = useWatch({ control: form.control });
  const soId = values.sales_order_id || undefined;
  const { data: soDetail } = useSalesOrder(soId);
  const {
    formState: { errors },
  } = form;

  const editable = !isEdit || (plan ? canEditPlan(plan.status) : true);

  useEffect(() => {
    if (!isEdit || !plan || hydratedRef.current) return;
    form.reset(planToForm(plan));
    hydratedRef.current = true;
    setActiveStage(0);
  }, [isEdit, plan, form]);

  useEffect(() => {
    if (activeStage >= fields.length) setActiveStage(Math.max(0, fields.length - 1));
  }, [activeStage, fields.length]);

  const openSos = useMemo(
    () =>
      salesOrders.filter((s) =>
        ["draft", "confirmed", "in_progress", "partial"].includes(s.status),
      ),
    [salesOrders],
  );

  const activeMachines = useMemo(
    () => machines.filter((m) => m.status === "active" || m.status === "under_maintenance"),
    [machines],
  );

  async function onSave() {
    const ok = await form.trigger();
    if (!ok) return;
    const payload = form.getValues();
    if (isEdit && planId) {
      const updated = await updatePlan.mutateAsync({ id: planId, values: payload });
      navigate({ to: "/planning/$planId", params: { planId: updated.id } });
    } else {
      const created = await createPlan.mutateAsync(payload);
      navigate({ to: "/planning/$planId", params: { planId: created.id } });
    }
  }

  useDocumentSaveShortcut(() => {
    if (editable) void onSave();
  });

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (isEdit && plan && !canEditPlan(plan.status)) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-muted-foreground">This plan cannot be edited.</p>
        <Button asChild size="sm" variant="outline">
          <Link to="/planning/$planId" params={{ planId: plan.id }}>
            Back to plan
          </Link>
        </Button>
      </div>
    );
  }

  const saving = createPlan.isPending || updatePlan.isPending;
  const stageErr = errors.stages?.[activeStage];

  return (
    <DocumentPage>
      <DocumentHeader
        title={isEdit ? `Edit ${plan?.plan_no ?? "plan"}` : "New process plan"}
        subtitle="Stages · machines · planned inputs / outputs · mill assist"
        back={
          <Button type="button" variant="ghost" size="icon-sm" asChild>
            {isEdit && planId ? (
              <Link to="/planning/$planId" params={{ planId }}>
                <ArrowLeft />
              </Link>
            ) : (
              <Link to="/planning">
                <ArrowLeft />
              </Link>
            )}
          </Button>
        }
        actions={
          editable ? (
            <SuggestPlanButton
              form={form}
              processTypes={processTypes}
              machines={activeMachines}
              materials={materials}
              stockBatches={stockBatches}
              salesOrder={soDetail}
              shapes={allShapes}
            />
          ) : null
        }
      />

      <FormSection title="Plan">
        <FieldGrid>
          <Field label="Plan date" required span={3} error={errors.plan_date?.message}>
            <Input type="date" disabled={!editable} {...form.register("plan_date")} />
          </Field>
          <Field label="Sales order" span={5} hint="Optional demand link">
            <NativeSelect
              disabled={!editable}
              {...form.register("sales_order_id", {
                onChange: () => form.setValue("sales_order_line_id", ""),
              })}
            >
              <option value="">None</option>
              {openSos.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.so_number} — {s.client_name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="SO line" span={4}>
            <NativeSelect disabled={!editable || !soId} {...form.register("sales_order_line_id")}>
              <option value="">None</option>
              {(soDetail?.lines ?? []).map((line) => (
                <option key={line.id} value={line.id}>
                  #{line.line_no} {line.material_name}
                  {line.grade_name ? ` · ${line.grade_name}` : ""}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </FieldGrid>
      </FormSection>

      <FormSection title="Stages">
        <div className="mb-2 flex flex-wrap gap-1">
          {fields.map((field, index) => {
            const st = values.stages?.[index];
            const proc = processTypes.find((p) => p.id === st?.process_type_id);
            return (
              <button
                key={field.id}
                type="button"
                className={cn(
                  "rounded border px-2 py-1 text-2xs font-medium",
                  index === activeStage
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:bg-muted/50",
                )}
                onClick={() => setActiveStage(index)}
              >
                {index + 1}. {proc?.name ?? "Stage"}
              </button>
            );
          })}
          {editable ? (
            <Button
              type="button"
              size="xs"
              variant="outline"
              onClick={() => {
                append({
                  ...emptyPlanStage,
                  process_type_id: processTypes[0]?.id ?? "",
                  input_source: fields.length === 0 ? "inventory" : "previous_stage",
                  inputs: fields.length === 0 ? [{ ...emptyPlanInput }] : [],
                  outputs: [{ ...emptyPlanOutput }],
                });
                setActiveStage(fields.length);
              }}
            >
              <Plus />
              Stage
            </Button>
          ) : null}
        </div>

        {fields.length === 0 ? (
          <p className="text-xs text-muted-foreground">Add a process stage to continue.</p>
        ) : (
          <StageEditor
            form={form}
            index={activeStage}
            editable={editable}
            processTypes={processTypes}
            machines={activeMachines}
            materials={materials}
            grades={grades}
            catalogueShapes={allShapes}
            stockBatches={stockBatches.map((b) => ({
              id: b.id,
              batch_no: b.batch_no,
              qty: b.quantity,
              quantity: b.quantity,
              weight: b.weight,
              material_id: b.material_id,
              material_name: b.material_name,
              material_grade_id: b.material_grade_id,
              grade_name: b.grade_name,
              thickness: b.thickness,
              width: b.width,
              length: b.length,
              wt_per_m: b.wt_per_m,
              received_at: b.received_at,
              batch_status: b.batch_status,
              quality_status: b.quality_status,
              last_process_code: b.last_process_code,
              process_path: b.process_path,
              location_code: b.location_code,
            }))}
            stageError={stageErr}
            onRemove={() => {
              if (fields.length <= 1) return;
              remove(activeStage);
              setActiveStage(Math.max(0, activeStage - 1));
            }}
          />
        )}
        {typeof errors.stages?.message === "string" ? (
          <p className="mt-1 text-2xs text-destructive">{errors.stages.message}</p>
        ) : null}
      </FormSection>

      <CollapsibleBlock title="Notes" defaultOpen={Boolean(values.notes)}>
        <Textarea rows={2} disabled={!editable} {...form.register("notes")} />
      </CollapsibleBlock>

      <DocumentStickyBar>
        <div className="text-2xs text-muted-foreground">
          {fields.length} stage{fields.length === 1 ? "" : "s"} · Ctrl+S save
        </div>
        <div className="flex gap-1.5">
          <Button type="button" variant="outline" size="sm" asChild>
            {isEdit && planId ? (
              <Link to="/planning/$planId" params={{ planId }}>
                Cancel
              </Link>
            ) : (
              <Link to="/planning">Cancel</Link>
            )}
          </Button>
          <Button type="button" size="sm" disabled={!editable || saving} onClick={() => void onSave()}>
            {saving ? <Loader2 className="animate-spin" /> : null}
            Save plan
          </Button>
        </div>
      </DocumentStickyBar>
    </DocumentPage>
  );
}

function StageEditor({
  form,
  index,
  editable,
  processTypes,
  machines,
  materials,
  grades,
  catalogueShapes,
  stockBatches,
  stageError,
  onRemove,
}: {
  form: ReturnType<typeof useForm<ProcessPlanFormValues>>;
  index: number;
  editable: boolean;
  processTypes: { id: string; code?: string; name: string }[];
  machines: {
    id: string;
    code: string;
    name: string;
    process_type_id: string;
    status: string;
    max_width_mm?: number | null;
  }[];
  materials: { id: string; name: string }[];
  grades: { id: string; name: string }[];
  catalogueShapes: {
    id: string;
    size_label: string;
    od?: number | null;
    thickness?: number | null;
    mother_size_id?: string | null;
  }[];
  stockBatches: {
    id: string;
    batch_no: string;
    material_id?: string;
    material_name?: string | null;
    material_grade_id?: string | null;
    grade_name?: string | null;
    qty: number;
    quantity?: number;
    weight: number;
    thickness?: number | null;
    width?: number | null;
    length?: number | null;
    wt_per_m?: number | null;
    received_at?: string;
    batch_status?: string;
    quality_status?: string | null;
    last_process_code?: string | null;
    process_path?: string | null;
    location_code?: string | null;
  }[];
  stageError: unknown;
  onRemove: () => void;
}) {
  const processTypeId = useWatch({ control: form.control, name: `stages.${index}.process_type_id` });
  const inputSource = useWatch({ control: form.control, name: `stages.${index}.input_source` });
  const stageInputs = useWatch({ control: form.control, name: `stages.${index}.inputs` });
  const materialId = useWatch({
    control: form.control,
    name: `stages.${index}.outputs.0.material_id`,
  });
  const { data: shapes = [] } = useShapeSizes(materialId || undefined);

  const inputsArray = useFieldArray({ control: form.control, name: `stages.${index}.inputs` });
  const outputsArray = useFieldArray({ control: form.control, name: `stages.${index}.outputs` });

  const stageProcess = processTypes.find((p) => p.id === processTypeId);
  const stageProcessCode = stageProcess?.code ?? null;
  const compatibleBatches = useMemo(
    () => filterCompatibleInventory(stockBatches, stageProcessCode),
    [stockBatches, stageProcessCode],
  );
  const selectedInputIds = useMemo(
    () =>
      (stageInputs ?? [])
        .map((i) => i.inventory_batch_id)
        .filter((id): id is string => Boolean(id)),
    [stageInputs],
  );

  function syncInputsFromPicker(rows: PlanningStockBatch[]) {
    form.setValue(
      `stages.${index}.inputs`,
      rows.map((b) => ({
        inventory_batch_id: b.id,
        planned_qty: b.qty ?? b.quantity ?? 1,
        planned_weight: b.weight ?? 0,
      })),
    );
  }

  const machinesForProcess = machines.filter((m) => m.process_type_id === processTypeId);

  const err = stageError as
    | {
        process_type_id?: { message?: string };
        machine_id?: { message?: string };
        input_source?: { message?: string };
        inputs?: { message?: string } | { inventory_batch_id?: { message?: string } }[];
        outputs?: { message?: string };
      }
    | undefined;

  return (
    <div className="space-y-2 rounded-md border border-border bg-card p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold">Stage {index + 1}</div>
        {editable ? (
          <Button type="button" variant="ghost" size="xs" onClick={onRemove}>
            <Trash2 />
            Remove stage
          </Button>
        ) : null}
      </div>

      <FieldGrid>
        <Field
          label="Process"
          required
          span={4}
          error={err?.process_type_id?.message}
          htmlFor={`st-${index}-proc`}
        >
          <NativeSelect
            id={`st-${index}-proc`}
            disabled={!editable}
            {...form.register(`stages.${index}.process_type_id`, {
              onChange: () => form.setValue(`stages.${index}.machine_id`, ""),
            })}
          >
            <option value="">Select…</option>
            {processTypes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field
          label="Machine"
          required
          span={4}
          error={err?.machine_id?.message}
          htmlFor={`st-${index}-mach`}
        >
          <NativeSelect
            id={`st-${index}-mach`}
            disabled={!editable}
            {...form.register(`stages.${index}.machine_id`)}
          >
            <option value="">Select…</option>
            {machinesForProcess.map((m) => (
              <option key={m.id} value={m.id}>
                {m.code} — {m.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field
          label="Input source"
          span={4}
          error={err?.input_source?.message}
          htmlFor={`st-${index}-src`}
        >
          <NativeSelect
            id={`st-${index}-src`}
            disabled={!editable || index === 0}
            {...form.register(`stages.${index}.input_source`, {
              onChange: (e) => {
                const v = e.target.value;
                if (v === "previous_stage") {
                  form.setValue(`stages.${index}.inputs`, []);
                } else if (!form.getValues(`stages.${index}.inputs`)?.length) {
                  form.setValue(`stages.${index}.inputs`, [{ ...emptyPlanInput }]);
                }
              },
            })}
          >
            {INPUT_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Start" span={3} htmlFor={`st-${index}-start`}>
          <Input
            id={`st-${index}-start`}
            type="date"
            disabled={!editable}
            {...form.register(`stages.${index}.planned_start_at`)}
          />
        </Field>
        <Field label="End" span={3} htmlFor={`st-${index}-end`}>
          <Input
            id={`st-${index}-end`}
            type="date"
            disabled={!editable}
            {...form.register(`stages.${index}.planned_end_at`)}
          />
        </Field>
        <Field
          label="QC required"
          span={3}
          hint="If on, next stage cannot consume until QC passed"
        >
          <label className="flex h-8 items-center gap-2 text-xs">
            <input
              type="checkbox"
              disabled={!editable}
              {...form.register(`stages.${index}.quality_check_required`)}
            />
            Yes — block next stage until verified
          </label>
        </Field>
      </FieldGrid>

      <MillStageAssist
        form={form}
        stageIndex={index}
        editable={editable}
        processTypes={processTypes}
        machines={machines}
        stockBatches={compatibleBatches as typeof stockBatches}
        materials={materials}
        shapes={catalogueShapes.length ? catalogueShapes : shapes}
      />

      {inputSource === "inventory" ? (
        <StageInventoryPicker
          stageProcessCode={stageProcessCode}
          batches={stockBatches}
          selectedIds={selectedInputIds}
          editable={editable}
          onSelectionChange={syncInputsFromPicker}
        />
      ) : null}

      {inputSource === "inventory" ? (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              Inputs
            </div>
            {editable ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                onClick={() => inputsArray.append({ ...emptyPlanInput })}
              >
                <Plus />
                Input
              </Button>
            ) : null}
          </div>
          {typeof err?.inputs === "object" && err.inputs && "message" in err.inputs ? (
            <p className="mb-1 text-2xs text-destructive">{err.inputs.message}</p>
          ) : null}
          <LineGrid>
            <LineGridHead>
              <LineGridTh className="w-[40%]">Batch</LineGridTh>
              <LineGridTh className="w-[20%]" align="right">
                Qty
              </LineGridTh>
              <LineGridTh className="w-[20%]" align="right">
                Weight
              </LineGridTh>
              <LineGridTh className="w-[10%]" />
            </LineGridHead>
            <LineGridBody>
              {inputsArray.fields.map((field, li) => (
                <LineGridRow key={field.id}>
                  <LineGridTd>
                    <NativeSelect
                      disabled={!editable}
                      className={cellControlClass()}
                      {...form.register(`stages.${index}.inputs.${li}.inventory_batch_id`, {
                        onChange: (e) => {
                          const batch = stockBatches.find((b) => b.id === e.target.value);
                          if (batch) {
                            form.setValue(`stages.${index}.inputs.${li}.planned_qty`, batch.qty);
                            form.setValue(
                              `stages.${index}.inputs.${li}.planned_weight`,
                              batch.weight,
                            );
                          }
                        },
                      })}
                    >
                      <option value="">Select batch…</option>
                      {compatibleBatches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.batch_no}
                          {b.material_name ? ` · ${b.material_name}` : ""}
                        </option>
                      ))}
                    </NativeSelect>
                  </LineGridTd>
                  <LineGridTd>
                    <Input
                      type="number"
                      step="0.001"
                      disabled={!editable}
                      className={cellControlClass()}
                      onWheel={blockNumberWheel}
                      {...form.register(`stages.${index}.inputs.${li}.planned_qty`)}
                    />
                  </LineGridTd>
                  <LineGridTd>
                    <Input
                      type="number"
                      step="0.001"
                      disabled={!editable}
                      className={cellControlClass()}
                      onWheel={blockNumberWheel}
                      {...form.register(`stages.${index}.inputs.${li}.planned_weight`)}
                    />
                  </LineGridTd>
                  <LineGridTd>
                    {editable ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => inputsArray.remove(li)}
                      >
                        <Trash2 />
                      </Button>
                    ) : null}
                  </LineGridTd>
                </LineGridRow>
              ))}
            </LineGridBody>
          </LineGrid>
        </div>
      ) : (
        <p className="text-2xs text-muted-foreground">
          Feeds from stage {index} output at execution (6.3).
        </p>
      )}

      <div>
        <div className="mb-1 flex items-center justify-between">
          <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
            Outputs
          </div>
          {editable ? (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => outputsArray.append({ ...emptyPlanOutput })}
            >
              <Plus />
              Output
            </Button>
          ) : null}
        </div>
        {typeof err?.outputs === "object" && err.outputs && "message" in err.outputs ? (
          <p className="mb-1 text-2xs text-destructive">{err.outputs.message}</p>
        ) : null}
        <LineGrid>
          <LineGridHead>
            <LineGridTh className="w-[22%]">Material</LineGridTh>
            <LineGridTh className="w-[14%]">Grade</LineGridTh>
            <LineGridTh className="w-[14%]">Size</LineGridTh>
            <LineGridTh className="w-[12%]" align="right">
              Qty
            </LineGridTh>
            <LineGridTh className="w-[12%]" align="right">
              Wt
            </LineGridTh>
            <LineGridTh className="w-[10%]" align="right">
              T
            </LineGridTh>
            <LineGridTh className="w-[10%]" align="right">
              W
            </LineGridTh>
            <LineGridTh className="w-[6%]" />
          </LineGridHead>
          <LineGridBody>
            {outputsArray.fields.map((field, li) => (
              <LineGridRow key={field.id}>
                <LineGridTd>
                  <NativeSelect
                    disabled={!editable}
                    className={cellControlClass()}
                    {...form.register(`stages.${index}.outputs.${li}.material_id`, {
                      onChange: () =>
                        form.setValue(`stages.${index}.outputs.${li}.shape_size_id`, ""),
                    })}
                  >
                    <option value="">Material…</option>
                    {materials.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </NativeSelect>
                </LineGridTd>
                <LineGridTd>
                  <NativeSelect
                    disabled={!editable}
                    className={cellControlClass()}
                    {...form.register(`stages.${index}.outputs.${li}.material_grade_id`)}
                  >
                    <option value="">—</option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </NativeSelect>
                </LineGridTd>
                <LineGridTd>
                  <NativeSelect
                    disabled={!editable}
                    className={cellControlClass()}
                    {...form.register(`stages.${index}.outputs.${li}.shape_size_id`)}
                  >
                    <option value="">—</option>
                    {shapes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.size_label}
                      </option>
                    ))}
                  </NativeSelect>
                </LineGridTd>
                <LineGridTd>
                  <Input
                    type="number"
                    step="0.001"
                    disabled={!editable}
                    className={cellControlClass()}
                    onWheel={blockNumberWheel}
                    {...form.register(`stages.${index}.outputs.${li}.planned_qty`)}
                  />
                </LineGridTd>
                <LineGridTd>
                  <Input
                    type="number"
                    step="0.001"
                    disabled={!editable}
                    className={cellControlClass()}
                    onWheel={blockNumberWheel}
                    {...form.register(`stages.${index}.outputs.${li}.planned_weight`)}
                  />
                </LineGridTd>
                <LineGridTd>
                  <Input
                    type="number"
                    step="0.001"
                    disabled={!editable}
                    className={cellControlClass()}
                    onWheel={blockNumberWheel}
                    {...form.register(`stages.${index}.outputs.${li}.thickness_mm`)}
                  />
                </LineGridTd>
                <LineGridTd>
                  <Input
                    type="number"
                    step="0.01"
                    disabled={!editable}
                    className={cellControlClass()}
                    onWheel={blockNumberWheel}
                    {...form.register(`stages.${index}.outputs.${li}.width_mm`)}
                  />
                </LineGridTd>
                <LineGridTd>
                  {editable && outputsArray.fields.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => outputsArray.remove(li)}
                    >
                      <Trash2 />
                    </Button>
                  ) : null}
                </LineGridTd>
              </LineGridRow>
            ))}
          </LineGridBody>
        </LineGrid>
      </div>
    </div>
  );
}
